import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { PlatformService } from '../service/platform/platform.service';
import { RetentionFeatureFlagsService } from '../service/retention/retention-feature-flags.service';
import { AuthService } from '../service/auth/auth.service';

/**
 * Routes where the mobile tab bar is suppressed. Auth screens render full-page
 * without the app shell; the test runner owns the bottom of the screen with
 * its own action bar (Cancel / prev / counter / next / Finish). Every other
 * route in the app is already protected by `AuthGuard`, so we don't need a
 * separate auth check here — and a non-reactive `auth.isAuthenticated()` call
 * inside a `computed()` would freeze the bar to its boot-time value anyway.
 */
const HIDDEN_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/auth/callback',
  '/logout',
  '/question-pool-study',
  '/question-pool-exam',
];

@Component({
  selector: 'app-mobile-tab-bar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './mobile-tab-bar.component.html',
  styleUrls: ['./mobile-tab-bar.component.css'],
})
export class MobileTabBarComponent {
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  protected readonly retentionFlags = inject(RetentionFeatureFlagsService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly visible = computed(() => {
    if (!this.platform.isMobile()) return false;
    const path = (this.currentUrl() || '/').split('?')[0].split('#')[0];
    return !HIDDEN_PATH_PREFIXES.some((h) => path === h || path.startsWith(h + '/'));
  });

  constructor() {
    effect(() => {
      const sid = this.authService.getUser()?.sid;
      if (sid) {
        void this.retentionFlags.ensureLoaded(sid);
      }
    });
    effect(() => {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('has-tab-bar', this.visible());
    });
  }
}
